import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

import {css, html, LitElement, PropertyValues} from 'lit';
import {styleMap} from 'lit-html/directives/style-map.js';
import {customElement, property} from 'lit/decorators.js';
import {when} from 'lit/directives/when.js';
import {pulsateAnimation} from '../../styles/animations/pulsate.animation';
import {scaleAnimation} from '../../styles/animations/scale.animation';
import {mobileHelper} from '../../styles/mobile-helper.styles';
import {SpLongPressController, SpLongPressOptions} from "../../controllers/sp-long-press.controller";
import {classMap} from "lit/directives/class-map.js";

@customElement('sp-button')
export class SpButton extends LitElement {
    @property()
    public containerPosition: "start" | "center" | "end" = 'start';
    @property({type: Boolean})
    public iconSide: 'left' | 'right' = 'left';
    @property({type: String})
    public icon: string;
    @property({type: String})
    public iconAreaWidth: string;
    @property({type: Number})
    public iconSizePercent = 45;
    @property({type: String})
    public iconAnimation: SpButtonIconAnimation;
    @property({type: String})
    public text: string;
    @property({type: String})
    public textPosition: SpButtonTextPosition = 'start';
    @property({type: String})
    public textToIconPadding: string = '0.5rem';
    @property({type: String})
    public textToBorderPadding: string = '1rem';
    @property({type: Boolean})
    public disabled = false;
    @property({type: Boolean})
    public showSpinner = false;
    @property({type: Boolean})
    public useLongPress = false;
    @property({type: Object})
    public longPressOptions: SpLongPressOptions;

    static styles = [
        mobileHelper,
        // language=CSS
        css`
            :host {
                display: flex;
                justify-content: start;
                position: relative;
                width: min-content;
                height: 3.4375rem;
                color: white;
                cursor: pointer;
                background-color: var(--sp-button-background-color);
                border-radius: var(--sp-default-border-radius);
                box-shadow: var(--sp-default-box-shadow);
            }

            :host([disabled]) {
                pointer-events: none;
                background-color: var(--sp-button-disabled-color);
            }
            
            .container {
                height: 100%;
                width: 100%;
                display: flex;
                font-size: 1.125rem;
                white-space: nowrap;
                justify-content: center;
            }
            
            .no-icon-area-width {
                aspect-ratio: 1;
            }
            
            /* This is a best effort approach for legacy browsers. It won't work nicely for buttons with custom height AND text */
            @supports not (aspect-ratio: 1) {
                .no-icon-area-width {
                    width: 3.4375rem;
                }
                .no-icon-area-width:only-child {
                    width: 100%;
                }
            }
            
            .bubble {
                border-radius: 50%;
            }
            
            .text {
                display: grid;
                flex-grow: 1;
            }

            .spinner-wrapper {
                width: 3.4375rem;
                height: 100%;
                display: grid;
                place-content: center;
            }

            sl-spinner {
                font-size: 1.7rem;
                --track-width: 0.3rem;
            }

            .icon-container {
                display: grid;
                height: 100%;
            }

            .icon {
                grid-column: 1 / 1;
                grid-row: 1 / 1;
                background-position: center center;
                height: 100%;
                background-repeat: no-repeat;
                z-index: 100;
                display: grid;
                place-content: center;
            }

            .anim-circle-sm {
                grid-column: 1 / 1;
                grid-row: 1 / 1;
                border-radius: 50%;
                height: 90%;
                aspect-ratio: 1 / 1;
                background-color: var(--sp-button-active-color);
                place-self: center;
                z-index: 2;
            }

            .anim-circle-lg {
                grid-column: 1 / 1;
                grid-row: 1 / 1;
                border-radius: 50%;
                height: 100%;
                aspect-ratio: 1 / 1;
                background-color: var(--sp-button-background-color);
                place-self: center;
                z-index: 1;
            }
        `,
        pulsateAnimation(),
        scaleAnimation({
            factor: 1.4,
            duration: 0.2
        }),
        scaleAnimation({
            className: 'scale-small-animation',
            factor: 2,
            duration: 0.2
        })
    ];

    longPressController;

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        // Only needed for legacy reasons in the spotteron-app, which uses fastclick.js and cannot set classes for nested components
        this.shadowRoot.host.classList.add('needsclick');
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (_changedProperties.has('useLongPress') || _changedProperties.has('longPressOption')) {
            if (this.useLongPress && !this.longPressController) {
                this.longPressController = new SpLongPressController(this, this.longPressOptions);
            } else if (!this.useLongPress && this.longPressController) {
                this.removeController(this.longPressController);
                this.longPressController = null;
            } else if (this.longPressController && _changedProperties.has('longPressOption')) {
                this.longPressController.updateOptions(this.longPressOptions);
            }
        }

    }

    protected updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (_changedProperties.has('containerPosition') && this.containerPosition) {
            this.style.justifyContent = this.containerPosition;
        }
    }

    protected render() {
        const textStyles = {
            'padding-left': `${this.iconSide === 'left' && this.icon ? this.textToIconPadding : this.textToBorderPadding}`,
            'padding-right': `${this.iconSide === 'right' && this.icon ? this.textToIconPadding : this.textToBorderPadding}`,
            'place-content': `${this.textPosition === 'end' ? 'center end' : this.textPosition === 'center' ? 'center' : 'center start'}`
        };
        return html`
            <div class="container"
                 style=${ styleMap({
                     ...this.containerPosition !== 'start' && { width: 'unset' }  
                 })}>
                ${when((this.iconSide === 'left' && this.icon) || (!this.icon && this.text && this.showSpinner), () => this.iconOrSpinnerTemplate())}
                ${when(this.text && (!(this.showSpinner && !this.icon)), () => html`
                    <div class="text" part="text" style=${styleMap(textStyles)}>${this.text}
                        <slot></slot>
                    </div>`)}
                ${when(this.iconSide === 'right' && this.icon, () => this.iconOrSpinnerTemplate())}
            </div>
        `;
    }

    private iconOrSpinnerTemplate() {
        const iconStyles = {
            'background-image': `${this.icon}`,
            'background-size': `auto ${this.iconSizePercent}%`
        };
        return html`
                <div part="icon-container"
                     class=${classMap({
                         'icon-container': true,
                         'no-icon-area-width': !this.iconAreaWidth })}
                     style=${styleMap({
                         ...this.iconAreaWidth && { 'width': this.iconAreaWidth }
                     })}>
                    <div class="icon" part="icon" style=${ when(!this.showSpinner, () => styleMap(iconStyles)) }>
                        ${ when(this.showSpinner, () => html`<sl-spinner></sl-spinner>`) }
                    </div>
                    ${when(!this.showSpinner && this.iconAnimation, () => this.iconAnimationTemplate())}
                </div>`;
    }

    private iconAnimationTemplate() {
        if (this.iconAnimation === 'pulsating-circle') {
            return html`
                <div class="anim-circle-sm pulsate-animation"></div>
                <div class="anim-circle-lg scale-animation"></div>
            `;
        }
    }
}

export type SpButtonIconAnimation = 'pulsating-circle';
export type SpButtonTextPosition = 'start' | 'end' | 'center';